import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import equal from 'fast-deep-equal';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { SITE_SETTINGS } from '@app/constants';
import { FeedbackService, NavigationService } from '@app/core/services';
import { BaseEntity } from '@app/models';
import { IEditService } from '@app/services';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { TitleService } from '../../core/services/title.service';
import { FormComponent } from '../forms/components/form.component';

@Component({
	template: '',
})
export abstract class BaseDetailsComponent<T extends BaseEntity, T2 extends IEditService<T>> implements OnInit, OnDestroy {
	@ViewChild(FormComponent) formComponent: FormComponent;

	entity: Partial<T>;
	form = new UntypedFormGroup({});
	id: number;

	protected readonly destroy$ = new Subject();
	protected entityService: T2;
	private readonly detailsPageTitle$ = new BehaviorSubject<string>(undefined);

	abstract entityName: string;
	abstract listingPagePath: string;
	abstract options: FormlyFormOptions;
	abstract fields: FormlyFieldConfig[];
	abstract pageTitleSuffix: string;
	abstract pageTitleProperty: keyof T;

	constructor(
		protected route: ActivatedRoute,
		protected location: Location,
		protected router: Router,
		protected dialog: MatDialog,
		protected feedbackService: FeedbackService,
		protected navigationService: NavigationService,
		protected titleService: TitleService
	) {}

	ngOnInit(): void {
		combineLatest([this.titleService.title$, this.detailsPageTitle$])
			.pipe(takeUntil(this.destroy$), distinctUntilChanged(equal))
			.subscribe(([, detailsTitle]) => {
				this.titleService.setTitle(detailsTitle);
			});

		this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
			this.id = Number(params.id);
			if (this.id > 0) {
				this.loadEntity();
			} else {
				const { entity } = this.location.getState() as any;
				this.entity = {
					...entity,
					Id: 0,
				};
				this.onEntityLoaded();

				if (this.formComponent) {
					this.formComponent.editForm.updateValueAndValidity();
				}
				this.setPageTitle();
			}

			this.onIdChanged();
		});

		this.loadOptions();
		this.afterOnInit();
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	edit(section: string): void {
		this.formComponent.edit(section);
	}

	delete(): void {
		if (!this.canDelete()) {
			return;
		}

		this.dialog
			.open(ModalComponent, {
				maxWidth: '800px',
				data: {
					title: 'Confirm Delete',
					message: `Are you sure you want to delete this ${this.entityName}?`,
				},
			})
			.afterClosed()
			.pipe(take(1))
			.subscribe((dialogResult) => {
				if (dialogResult) {
					this.entityService.deleteEntity(this.entity.Id).subscribe({
						next: () => {
							this.afterDelete();
							this.router.navigate([this.listingPagePath]);
							this.feedbackService.success('deleted', this.entityName);
						},
						error: (errorResponse) => {
							const errorMessage = errorResponse.error?.ExceptionMessage || errorResponse.error?.Message || '';
							this.feedbackService.error(`There was a problem deleting the ${this.entityName}: ${errorMessage}`);
						},
					});
				}
			});
	}

	duplicate(): void {
		this.router.navigate([`${this.listingPagePath}/0`], {
			queryParams: { openFlyout: 1, sections: ['details'], cancelRedirect: [`${this.listingPagePath}/${this.entity.Id}`] },
			state: { entity: { ...this.entity, Id: 0 } },
		});
	}

	public onSubmit = (model: T): void => {
		if (this.entity.Id > 0) {
			this.entityService.updateEntity(model).subscribe({
				next: (data) => {
					this.entity = data || model;
					this.feedbackService.success('updated', this.entityName);
					this.form.reset({ ...this.entity });
					this.formComponent.closeFlyout();
					this.setPageTitle();
					this.afterSubmit();
				},
				error: (errorResponse) => {
					const errorMessage = errorResponse.error?.ExceptionMessage || errorResponse.error?.Message || '';
					this.feedbackService.error(`There was a problem updating the ${this.entityName}: ${errorMessage}`);
				},
			});
		} else {
			this.entityService.createEntity({ ...model, Id: 0 }).subscribe((data) => {
				this.entity = data;
				this.feedbackService.success('created', this.entityName);
				this.form.reset({ ...this.entity });
				this.formComponent.closeFlyout();
				this.router.navigate([`${this.listingPagePath}/`, this.entity.Id]);
				this.setPageTitle();
				this.afterSubmit();
			});
		}
	};

	protected afterOnInit(): void {
		//to be overridden by subclasses where needed
	}

	protected onIdChanged(): void {
		//to be overridden by subclasses where needed
	}

	protected onEntityLoaded(): void {
		//to be overridden by subclasses where needed
	}

	protected loadOptions(): void {
		//to be overridden by subclasses where needed
	}

	protected canDelete(): boolean {
		//to be overridden by subclasses where needed
		return true;
	}

	protected afterDelete(): void {
		//to be overridden by subclasses where needed
	}

	protected afterSubmit(): void {
		//to be overridden by subclasses where needed
	}

	protected loadEntity(): void {
		this.entityService.getById(this.id).subscribe({
			next: (data: T) => {
				if (!data) {
					this.router.navigate([this.listingPagePath]);
					return;
				}
				this.entity = data;
				this.setPageTitle();
				this.onEntityLoaded();
			},
			error: (errorResponse) => {
				const errorMessage = errorResponse.error?.ExceptionMessage || errorResponse.error?.Message || '';
				this.feedbackService.error(`There was a problem loading the ${this.entityName}: ${errorMessage}`);
			},
		});
	}

	protected setPageTitle(): void {
		let title = `${this.pageTitleSuffix} - ${SITE_SETTINGS.name}`;
		if (this.entity.Id > 0) {
			title = `${this.entity[this.pageTitleProperty]} - ${title}`;
		}
		this.detailsPageTitle$.next(title);
	}
}
